import { Box, ColumnLayout, Container, Link, SpaceBetween, Spinner, StatusIndicator, Table } from '@amzn/awsui-components-react';
import React from 'react';
import { TelemetryEvents, TELEMETRY_EVENT_TYPES } from '../../common/TelemetryEvents';
import { useTrackUserEvent } from '../../common/telemetry';
import { formatDuration, formatFullDate, formatShortDate, getRemainingDays, hasAccessExpired } from '../../common';
import { AttendanceStatus, useLearnerAttendance } from '../../hooks/cloudQuest/useLearnerAttendance';
import { PremiumContentItem, usePremiumContentGroup } from '../../hooks/premiumContent/usePremiumContentGroup';

/** Every string the tab can show, kept together so wording changes do not mean reading the logic. */
const PREMIUM_CONTENT_MESSAGES = {
    LOADING: 'Checking your access to premium content…',
    ERROR: 'Unable to check your access to premium content. Please try again later.',
    GATED: 'You can access this tab when you get marked either "Attended" or "Partially Attended" after class ended',
    EXPIRED_HEADING: 'Your access to premium content has expired',
    EXPIRED_BODY: (lastDay: string) => `The last day of access was ${lastDay}.`,
    LIST_ERROR: 'Unable to load premium content. Please try again later.',
    EMPTY_TABLE: 'No premium content available',
} as const;

export enum PremiumAccessState {
    LOADING = 'LOADING',
    ERROR = 'ERROR',
    GATED = 'GATED',
    EXPIRED = 'EXPIRED',
    GRANTED = 'GRANTED',
}

interface PremiumAccessInputs {
    loading: boolean;
    hasError: boolean;
    hasExpired: boolean;
    attendance: AttendanceStatus | null;
}

/**
 * Single place the access decision is made, so the render path only has to display the outcome.
 *
 * Attendance alone decides access — deliberately not the schedule. An instructor who wraps up early
 * marks attendance before the last session's scheduled end, and those learners should get the
 * content then rather than waiting for the clock.
 *
 * Order is deliberate. A failed read is not the same as not being marked, and expiry is terminal so
 * it outranks attendance, because telling a learner to get marked would point them at something
 * that can no longer unlock the content.
 */
export const resolvePremiumAccessState = ({ loading, hasError, hasExpired, attendance }: PremiumAccessInputs): PremiumAccessState => {
    if (loading) return PremiumAccessState.LOADING;
    if (hasError) return PremiumAccessState.ERROR;
    if (hasExpired) return PremiumAccessState.EXPIRED;
    if (attendance === AttendanceStatus.ATTENDED || attendance === AttendanceStatus.PARTIALLY_ATTENDED) {
        return PremiumAccessState.GRANTED;
    }
    return PremiumAccessState.GATED;
};

/** Same rounded container the granted state uses, so the states swap in place. */
const MessagePanel = ({ children, testId }: { children: React.ReactNode; testId: string }) => (
    <Container>
        <Box textAlign='center' padding='l' data-testid={testId}>
            {children}
        </Box>
    </Container>
);

const AccessMessage = ({ state, accessEndTimestamp }: { state: PremiumAccessState; accessEndTimestamp?: number }) => {
    switch (state) {
        case PremiumAccessState.LOADING:
            return (
                <MessagePanel testId='premium-content-loading'>
                    <Spinner size='large' />
                    <Box variant='p' color='text-body-secondary'>
                        {PREMIUM_CONTENT_MESSAGES.LOADING}
                    </Box>
                </MessagePanel>
            );
        case PremiumAccessState.ERROR:
            return (
                <MessagePanel testId='premium-content-error'>
                    <StatusIndicator type='error'>{PREMIUM_CONTENT_MESSAGES.ERROR}</StatusIndicator>
                </MessagePanel>
            );
        case PremiumAccessState.EXPIRED:
            return (
                <MessagePanel testId='premium-content-expired'>
                    <SpaceBetween size='xs'>
                        <Box variant='h3'>{PREMIUM_CONTENT_MESSAGES.EXPIRED_HEADING}</Box>
                        <Box variant='p' color='text-body-secondary'>
                            {PREMIUM_CONTENT_MESSAGES.EXPIRED_BODY(formatFullDate(accessEndTimestamp))}
                        </Box>
                    </SpaceBetween>
                </MessagePanel>
            );
        default:
            return (
                <MessagePanel testId='premium-content-gated'>
                    <Box variant='p' color='text-body-secondary'>
                        {PREMIUM_CONTENT_MESSAGES.GATED}
                    </Box>
                </MessagePanel>
            );
    }
};

export interface PremiumContentTabProps {
    userId?: string;
    learningActivityId?: string;
    accessEndTimestamp?: number;
}

export const PremiumContentTab = ({ userId, learningActivityId, accessEndTimestamp }: PremiumContentTabProps) => {
    const trackUserEvent = useTrackUserEvent();
    const { data, loading, error } = useLearnerAttendance(userId, learningActivityId);
    const { items, loading: itemsLoading, error: itemsError } = usePremiumContentGroup();

    // Display only, and null when there is no access window so there is no countdown to show.
    const daysRemaining = getRemainingDays(accessEndTimestamp);

    const accessState = resolvePremiumAccessState({
        loading,
        hasError: !!error,
        hasExpired: hasAccessExpired(accessEndTimestamp),
        attendance: data?.userById?.attendanceForClass ?? null,
    });

    if (accessState !== PremiumAccessState.GRANTED) {
        return <AccessMessage state={accessState} accessEndTimestamp={accessEndTimestamp} />;
    }

    // Only reached once access is granted, so the content list state is kept out of the access
    // decision above: a learner who is not eligible does not need to know whether the list loaded.
    if (itemsLoading) {
        return (
            <MessagePanel testId='premium-content-list-loading'>
                <Spinner size='large' />
            </MessagePanel>
        );
    }

    // A failed list read is not an empty list, so it does not fall through to the table's empty
    // message, which would tell an eligible learner there is nothing available.
    if (itemsError) {
        return (
            <MessagePanel testId='premium-content-list-error'>
                <StatusIndicator type='error'>{PREMIUM_CONTENT_MESSAGES.LIST_ERROR}</StatusIndicator>
            </MessagePanel>
        );
    }

    return (
        <SpaceBetween size='l'>
            {daysRemaining !== null && (
                <Container>
                    <ColumnLayout columns={2} variant='text-grid'>
                        <div>
                            <Box variant='awsui-key-label'>Days remaining</Box>
                            <Box variant='h1' color='text-status-info' data-testid='premium-days-remaining'>
                                {daysRemaining}
                            </Box>
                        </div>
                        <div>
                            <Box variant='awsui-key-label'>End date</Box>
                            <Box variant='h1' color='text-status-info' data-testid='premium-end-date'>
                                {formatShortDate(accessEndTimestamp)}
                            </Box>
                        </div>
                    </ColumnLayout>
                </Container>
            )}

            <Table
                variant='container'
                items={items}
                trackBy='catalogId'
                data-testid='premium-content-table'
                columnDefinitions={[
                    {
                        id: 'title',
                        header: 'Title',
                        cell: (item: PremiumContentItem) => (
                            <Link
                                external
                                href={item.launchUrl}
                                target='_blank'
                                data-testid={`premium-content-link-${item.catalogId}`}
                                onFollow={() =>
                                    trackUserEvent(
                                        TELEMETRY_EVENT_TYPES.CLICK,
                                        TelemetryEvents.COURSE_DETAIL_PREMIUM_CONTENT_LAUNCH_CLICK,
                                        {
                                            catalogId: item.catalogId,
                                            title: item.title,
                                        },
                                    )
                                }
                            >
                                {item.title}
                            </Link>
                        ),
                    },
                    {
                        id: 'duration',
                        header: 'Duration',
                        cell: (item: PremiumContentItem) => formatDuration(item.durationMinutes),
                    },
                ]}
                empty={
                    <Box textAlign='center' color='text-body-secondary' padding='l'>
                        {PREMIUM_CONTENT_MESSAGES.EMPTY_TABLE}
                    </Box>
                }
            />
        </SpaceBetween>
    );
};
